/**
 * @file
 * Toggles required state on vehicle detail textfields (Webform #states required
 * does not apply to inputs inside the states wrapper).
 */

(function ($, Drupal, once, drupalSettings) {
  'use strict';

  /**
   * Neo renders a literal "Optional" span when #required is false at build time.
   */
  function setFieldRequired($input, required, optionalLabel) {
    const $item = $input.closest('.js-form-item');
    let $label = $item.find('label.form-item__label').first();
    if (!$label.length) {
      $label = $item.find('label').first();
    }

    $input.prop('required', required);
    if (required) {
      $input.attr('aria-required', 'true');
      $label.addClass('js-form-required form-required');
      $label.find('.optional').remove();
    }
    else {
      $input.removeAttr('required aria-required');
      if ($input[0]?.setCustomValidity) {
        $input[0].setCustomValidity('');
      }
      $label.removeClass('js-form-required form-required');
      if (optionalLabel && !$label.find('.optional').length && $label.length) {
        $label.append(document.createTextNode('\u00a0'));
        $label.append($('<span class="optional"></span>').text(optionalLabel));
      }
    }
  }

  /**
   * Required label styling without HTML5 required (admin node form).
   */
  function setFieldVisualRequired($input) {
    const $item = $input.closest('.js-form-item');
    let $label = $item.find('label.form-item__label').first();
    if (!$label.length) {
      $label = $item.find('label').first();
    }
    $label.addClass('js-form-required form-required');
    $label.find('.optional').remove();
    $input.prop('required', false);
    $input.removeAttr('required aria-required');
    if ($input[0]?.setCustomValidity) {
      $input[0].setCustomValidity('');
    }
  }

  function clearHtml5Required($input, optionalLabel) {
    setFieldRequired($input, false, optionalLabel);
  }

  function isInputVisible(input) {
    if (!input || input.disabled) {
      return false;
    }
    if (input.offsetParent !== null) {
      return true;
    }
    return input.getClientRects().length > 0;
  }

  /**
   * @param {jQuery} $form
   * @param {object} settings
   */
  function readVehicleType($form, settings) {
    const selectors = settings.vehicleTypeSelectors
      || (settings.vehicleType ? [settings.vehicleType] : []);
    for (let i = 0; i < selectors.length; i += 1) {
      const value = $form.find(':input[name="' + selectors[i] + '"]').val();
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        return String(value).trim();
      }
    }
    return '';
  }

  function applyLicensePlateCountryDefault($form, settings) {
    const fieldName = settings.licensePlateCountryField;
    const defaultValue = settings.licensePlateCountryDefault;
    if (!fieldName || !defaultValue) {
      return;
    }
    const $input = $form.find(':input[name="' + fieldName + '"]');
    if (!$input.length) {
      return;
    }
    if (String($input.val() ?? '').trim() === '') {
      $input.val(String(defaultValue));
    }
  }

  /**
   * Equalize vehicle plate label heights so wrapped translations align inputs.
   *
   * @param {jQuery} $form
   */
  function syncVehiclePlateLabelHeights($form) {
    const $row = $form.find('[id^="edit-flexbox-vehicle-plate"]').first();
    if (!$row.length) {
      return;
    }

    const selectors = [
      '.form-item-license-plate-country.form-item:not(.form-type-boolean) > label',
      '.form-item-license-plate-country.form-item:not(.form-type-boolean) > .form-item__label',
      '.form-item-license-plate.form-item:not(.form-type-boolean) > label',
      '.form-item-license-plate.form-item:not(.form-type-boolean) > .form-item__label',
    ];
    const $labels = $row.find(selectors.join(',')).filter(':visible');
    if (!$labels.length) {
      return;
    }

    $labels.css('min-height', '');
    let maxHeight = 0;
    $labels.each((_, label) => {
      maxHeight = Math.max(maxHeight, Math.ceil(label.getBoundingClientRect().height));
    });
    if (maxHeight > 0) {
      $labels.css('min-height', maxHeight + 'px');
    }
  }

  function updateFormVehicleDetails($form, settings) {
    applyLicensePlateCountryDefault($form, settings);

    const vehicleType = readVehicleType($form, settings);
    const html5Required = settings.html5Required !== false;
    const logicallyRequired = vehicleType !== '' && vehicleType !== 'bike';

    settings.fields.forEach((fieldName) => {
      const $input = $form.find(':input[name="' + fieldName + '"]');
      if (!$input.length) {
        return;
      }
      if (!isInputVisible($input[0])) {
        clearHtml5Required($input, settings.optionalLabel);
        return;
      }
      if (logicallyRequired) {
        if (html5Required) {
          setFieldRequired($input, true, settings.optionalLabel);
        }
        else {
          setFieldVisualRequired($input);
        }
        return;
      }
      clearHtml5Required($input, settings.optionalLabel);
    });

    syncVehiclePlateLabelHeights($form);
  }

  function triggerVehicleTypeChange($form, settings) {
    const selectors = settings.vehicleTypeSelectors
      || (settings.vehicleType ? [settings.vehicleType] : []);
    selectors.forEach((name) => {
      const $input = $form.find(':input[name="' + name + '"]');
      if ($input.length) {
        $input.trigger('change');
      }
    });
  }

  /**
   * Strip HTML5 required from hidden controls before native validation runs.
   *
   * @param {HTMLFormElement} form
   */
  function clearRequiredOnHiddenInputs(form) {
    form.querySelectorAll(':input[required]').forEach((input) => {
      if (!isInputVisible(input)) {
        input.required = false;
        input.removeAttribute('required');
        input.removeAttribute('aria-required');
        if (input.setCustomValidity) {
          input.setCustomValidity('');
        }
      }
    });
  }

  /**
   * @param {object} settings
   * @param {string} formSelector
   * @param {string} onceKey
   * @param {string} context
   */
  function attachVehicleDetailsBehavior(settings, formSelector, onceKey, context) {
    if (!settings?.fields?.length) {
      return;
    }

    const vehicleTypeSelectors = settings.vehicleTypeSelectors
      || (settings.vehicleType ? [settings.vehicleType] : []);
    if (!vehicleTypeSelectors.length) {
      return;
    }

    const selectorList = vehicleTypeSelectors.map((name) => ':input[name="' + name + '"]').join(',');

    $(once(onceKey, formSelector, context)).each(function () {
      const $form = $(this);
      $form.on('change.parkimmobVehicleDetails', selectorList, () => {
        updateFormVehicleDetails($form, settings);
      });
      updateFormVehicleDetails($form, settings);
      $(window).on('resize.parkimmobVehicleDetails', () => {
        syncVehiclePlateLabelHeights($form);
      });

      if ($form[0] && !$form[0].dataset.parkimmobVehicleSubmitGuard) {
        $form[0].dataset.parkimmobVehicleSubmitGuard = '1';
        $form[0].addEventListener('submit', () => {
          clearRequiredOnHiddenInputs($form[0]);
        }, true);
      }
    });
  }

  /**
   * Gin and other admin themes validate via submit button click before submit.
   *
   * @param {object} settings
   */
  function attachSubmitButtonGuard(settings) {
    if (!settings?.formSelector) {
      return;
    }
    $(once('parkimmob-vehicle-details-submit-guard', 'html')).each(() => {
      $(document).on('click.parkimmobVehicleDetails', 'input[type="submit"], button[type="submit"]', function () {
        const formId = this.getAttribute('form');
        const form = formId ? document.getElementById(formId) : this.form;
        if (!form || !form.matches(settings.formSelector)) {
          return;
        }
        clearRequiredOnHiddenInputs(form);
      });
    });
  }

  Drupal.behaviors.parkimmobRequestSubscriptionVehicleDetails = {
    attach(context) {
      const subs = drupalSettings.parkimmobSubscriptions || {};

      attachVehicleDetailsBehavior(
        subs.requestSubscriptionVehicleDetails,
        'form.webform-submission-form',
        'parkimmob-request-subscription-vehicle-details',
        context,
      );

      const nodeSettings = subs.subscriptionNodeVehicleDetails;
      if (nodeSettings?.formSelector) {
        attachVehicleDetailsBehavior(
          nodeSettings,
          nodeSettings.formSelector,
          'parkimmob-subscription-node-vehicle-details',
          context,
        );
        attachSubmitButtonGuard(nodeSettings);
      }

      const webformSettings = subs.requestSubscriptionVehicleDetails;
      const hasWebform = Boolean(webformSettings?.fields?.length);
      const hasNode = Boolean(nodeSettings?.formSelector);

      if (!hasWebform && !hasNode) {
        return;
      }

      // Re-sync after AJAX (location / vehicle type wrappers).
      $(once('parkimmob-vehicle-details-ajax', 'html')).each(() => {
        $(document).on('ajaxComplete.parkimmobVehicleDetails', () => {
          if (hasWebform) {
            $('form.webform-submission-form').each(function () {
              updateFormVehicleDetails($(this), webformSettings);
            });
          }
          if (hasNode && nodeSettings?.formSelector) {
            $(nodeSettings.formSelector).each(function () {
              const $form = $(this);
              triggerVehicleTypeChange($form, nodeSettings);
              updateFormVehicleDetails($form, nodeSettings);
            });
          }
        });
      });

      // #states visibility toggles run after change; sync required markers then.
      $(once('parkimmob-vehicle-details-states', 'html')).each(() => {
        $(document).on('state:visible state:invisible', (event) => {
          if (!event.trigger) {
            return;
          }
          if (hasWebform) {
            $('form.webform-submission-form').each(function () {
              updateFormVehicleDetails($(this), webformSettings);
            });
          }
          if (hasNode && nodeSettings?.formSelector) {
            $(nodeSettings.formSelector).each(function () {
              updateFormVehicleDetails($(this), nodeSettings);
            });
          }
        });
      });
    },
  };

})(jQuery, Drupal, once, drupalSettings);
